
import React from 'react';
import { Phone, Users, Shield, Flame, Zap, Ambulance } from 'lucide-react';
import { ChevronRight } from 'lucide-react';
import logo from '/lovable-uploads/40a4ee59-7714-4091-a6f7-e5ba5819fe5f.png';
import PageLayout from '@/components/PageLayout';
import AdBanner from '@/components/AdBanner';
import { useTranslation } from 'react-i18next';

interface ContactItemProps {
  icon: React.ReactNode;
  title: string;
  phone?: string;
  color?: string;
  bgColor?: string;
}

const ContactItem: React.FC<ContactItemProps> = ({ icon, title, phone, color = "text-gray-500", bgColor = "bg-gray-100" }) => {
  const { t } = useTranslation();

  const handleClick = () => {
    if (phone) {
      window.location.href = `tel:${phone}`;
      console.log(`${t('contacts.title')} - ${title}: ${phone}`);
    }
  };

  return (
    <div 
      className="flex items-center justify-between p-4 border-b cursor-pointer hover:bg-gray-50 transition-colors rounded-lg my-2 shadow-sm"
      onClick={handleClick}
      style={{ background: 'linear-gradient(to right, #ffffff, #f8f9fa)' }}
    >
      <div className="flex items-center">
        <div className={`w-12 h-12 flex items-center justify-center rounded-full ${bgColor} ${color}`}>
          {icon}
        </div>
        <span className="text-lg ml-4 font-medium">{title}</span>
      </div>
      <div className="flex items-center">
        <span className="text-sm text-primary mr-2 font-medium">{phone}</span>
        <ChevronRight className="text-gray-400" size={20} />
      </div>
    </div>
  );
};

const Contacts = () => {
  const { t } = useTranslation();
  
  return (
    <PageLayout 
      title={t('contacts.title')} 
      logo={logo}
      showBackButton
      colorScheme="bg-gradient-to-r from-orange-500 to-red-500"
    >
      {/* Ad Banner for contacts page */}
      <AdBanner pageSlug="contacts" />
      
      <div className="bg-gradient-to-r from-orange-100 to-red-100 p-4 rounded-lg shadow-sm mb-4">
        <h2 className="text-xl text-orange-700 font-semibold">{t('contacts.djiboutiTelecom')}</h2>
      </div>
      
      <ContactItem 
        icon={<Phone size={24} />} 
        title={t('contacts.breakdown')} 
        phone="13"
        color="text-blue-600" 
        bgColor="bg-blue-100"
      />
      
      <ContactItem 
        icon={<Users size={24} />} 
        title={t('contacts.customer')} 
        phone="21321695"
        color="text-green-600"
        bgColor="bg-green-100" 
      />
      
      <div className="bg-gradient-to-r from-red-100 to-orange-100 p-4 rounded-lg shadow-sm mb-4 mt-6">
        <h2 className="text-xl text-red-700 font-semibold">{t('contacts.emergencyNumbers')}</h2>
      </div>
      
      <ContactItem 
        icon={<Flame size={24} />} 
        title={t('contacts.fireDepartment')} 
        phone="18"
        color="text-red-600"
        bgColor="bg-red-100" 
      />
      
      <ContactItem 
        icon={<Shield size={24} />} 
        title={t('contacts.police')} 
        phone="17"
        color="text-blue-600"
        bgColor="bg-blue-100" 
      />
      
      <ContactItem 
        icon={<Zap size={24} />} 
        title={t('contacts.eddBreakdown')} 
        phone="1O24"
        color="text-yellow-600"
        bgColor="bg-yellow-100" 
      />
      
      <ContactItem 
        icon={<Ambulance size={24} />} 
        title={t('contacts.ambulance')} 
        phone="19"
        color="text-red-600"
        bgColor="bg-red-100" 
      />
    </PageLayout>
  );
};

export default Contacts;
